/**
 * AnimatedRoute - Pixel-based animated drawing for the animation overlay pipeline
 *
 * Renders a single drawing path with optional progress animation (strokeDashoffset).
 * Used by AnimationCanvasRenderer for route visualization during playback.
 */

import { useMemo } from 'react'
import type { Coordinate, Drawing } from '@/types'
import type { FieldCoordinateSystem } from '@/utils'
import { buildSVGPath } from '@/utils/SvgPath.utils'
import { useTheme } from '@/contexts/SettingsContext'
import { getThemeAwareColor } from '@/utils/ColorUtils.ts'
import { FilledLineEnding } from '@/components/canvas/FilledLineEnding'
import {
	DASH_PATTERN_GAP_MULTIPLIER,
	DASH_PATTERN_LENGTH_MULTIPLIER,
	LINE_END_NONE,
} from '@/constants'
import '@/styles/field-colors.css'

type AnimatedRouteProps = {
	drawing: Drawing
	coordSystem: FieldCoordinateSystem
	progress?: number
	showProgress?: boolean
	opacity?: number
	/** Hide the dimmed ghost path behind the animated route */
	hideGhostPath?: boolean
}

export function AnimatedRoute({
	drawing,
	coordSystem,
	progress = 0,
	showProgress = false,
	opacity = 1,
	hideGhostPath = false,
}: AnimatedRouteProps) {
	const { theme } = useTheme()

	// Build the full SVG path with length calculation
	const { d, pathLength = 0, endDirection } = useMemo(() => {
		const transformPoint = (point: Coordinate): Coordinate => {
			return coordSystem.feetToPixels(point.x, point.y)
		}
		return buildSVGPath(drawing, transformPoint, { calculateLength: true })
	}, [drawing, coordSystem])

	if (!d) return null

	const displayColor = getThemeAwareColor(drawing.style.color, theme)
	const strokeWidth = drawing.style.strokeWidth * coordSystem.scale

	const lineDash =
		drawing.style.lineStyle === 'dashed'
			? [
				strokeWidth * DASH_PATTERN_LENGTH_MULTIPLIER,
				strokeWidth * DASH_PATTERN_GAP_MULTIPLIER,
			]
			: []

	// Calculate dash offset for progress animation
	const progressOffset = showProgress ? pathLength * (1 - progress) : 0

	const showGhost = !hideGhostPath || !showProgress
	const routeComplete = showProgress && progress >= 0.98

	const hasEnding = drawing.style.lineEnd !== LINE_END_NONE && endDirection

	return (
		<g opacity={opacity} pointerEvents="none">
			{/* Ghost layer: group opacity composites route + ending as one unit,
			    preventing overlap artifacts at the junction */}
			{showGhost && (
				<g opacity={showProgress ? 0.3 : 1}>
					<path
						d={d}
						fill="none"
						stroke="black"
						strokeWidth={strokeWidth}
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeDasharray={lineDash.length > 0 ? lineDash.join(' ') : undefined}
					/>
					{hasEnding && (
						<FilledLineEnding
							lineEnd={drawing.style.lineEnd as 'arrow' | 'tShape'}
							point={endDirection!.point}
							angle={endDirection!.angle}
							strokeWidth={strokeWidth}
							color="black"
						/>
					)}
				</g>
			)}

			{/* Traveled portion (animated) using CSS variable */}
			{showProgress && progress > 0 && (
				<path
					d={d}
					fill="none"
					stroke="var(--progress-traveled)"
					strokeWidth={strokeWidth}
					strokeLinecap="round"
					strokeLinejoin="round"
					strokeDasharray={`${pathLength}`}
					strokeDashoffset={progressOffset}
				/>
			)}

			{/* Filled ending in progress color when route completes */}
			{routeComplete && hasEnding && (
				<FilledLineEnding
					lineEnd={drawing.style.lineEnd as 'arrow' | 'tShape'}
					point={endDirection!.point}
					angle={endDirection!.angle}
					strokeWidth={strokeWidth}
					color="var(--progress-traveled)"
				/>
			)}
		</g>
	)
}
